import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'

import { ApiError, useAuth } from '@forbiz/web-kit'
import type { OccasionType, OrgCatalogItem } from '@forbiz/contracts'

import { orgCatalogApi } from '../lib/orgCatalog.js'
import { defaultProductImage } from '../lib/productImage.js'
import { useAsync } from '../lib/useAsync.js'
import s from './ProductCuration.module.css'

/**
 * 상품 관리 — 조직 즐겨찾기 셀프 큐레이션 (BIZ-83). 기업 관리자 전용.
 *
 * 전역 상품에서 별표(★)로 주문서에 쓸 상품을 고른다. 별표가 하나라도 있으면 회사 전체
 * 주문서가 그 목록으로 좁혀진다(BIZ-82 폴백) — 그래서 상단 배너로 "지금 주문서에 뭐가
 * 보이는지"를 상시 알린다(첫 별표의 절벽 방지). 운영자 협의(협의단가 등) 상품은 잠금.
 */

const OCC_TABS: { value: OccasionType; label: string }[] = [
  { value: 'FUNERAL', label: '근조' },
  { value: 'CELEBRATION', label: '축하' },
  { value: 'GIFT', label: '난·기타' },
]

export function ProductCurationPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'COMPANY_ADMIN'

  const { data, loading, error, reload } = useAsync(() => orgCatalogApi.list(), [])
  const [items, setItems] = useState<OrgCatalogItem[]>([])
  const [tab, setTab] = useState<OccasionType>('FUNERAL')
  const [busyId, setBusyId] = useState<number | null>(null)
  const [clearing, setClearing] = useState(false)
  const [toast, setToast] = useState('')

  useEffect(() => {
    if (data) setItems(data.items)
  }, [data])

  // 직원(관리자 아님)은 메뉴에 노출되지 않지만 URL 직접 진입도 막는다.
  if (user && !isAdmin) return <Navigate to="/" replace />

  const starredCount = items.filter((i) => i.starred).length
  const total = items.length
  const curated = starredCount > 0
  // 노출 중 수 — 큐레이션했으면 켠 것만, 안 했으면 전체가 보인다(BIZ-82 폴백).
  const shownCount = curated ? starredCount : total
  const shown = items.filter((i) => i.occasionType === tab)
  // 유형별 노출/전체. 폴백이 전역 all-or-nothing이라, 큐레이션 후 어떤 유형이 0이면 그 유형은
  // 주문서에서 통째로 안 보인다(직원이 그 유형을 주문 못 함) — 유형별 카운트로 이 구멍을 드러낸다.
  const occStat = (occ: OccasionType) => {
    const inOcc = items.filter((i) => i.occasionType === occ)
    return { total: inOcc.length, exposed: curated ? inOcc.filter((i) => i.starred).length : inOcc.length }
  }
  // 큐레이션했는데 노출 0인 유형 = 주문 불가 경고 대상.
  const emptyCats = curated ? OCC_TABS.filter((t) => { const st = occStat(t.value); return st.total > 0 && st.exposed === 0 }) : []

  async function mutate(p: OrgCatalogItem, body: { starred: boolean; isDefault?: boolean }) {
    setBusyId(p.productId)
    setToast('')
    const prev = items
    // 낙관적 반영. 기본 지정(스왑)은 서버 정합이 필요해 성공 후 reload로 맞춘다.
    setItems((cur) =>
      cur.map((it) => {
        if (it.productId === p.productId) return { ...it, starred: body.starred, isDefault: body.isDefault ? true : body.starred ? it.isDefault : false }
        if (body.isDefault) return { ...it, isDefault: false } // 기본은 하나뿐
        return it
      }),
    )
    try {
      await orgCatalogApi.setStar(p.productId, body)
      if (body.isDefault) reload()
    } catch (err) {
      setItems(prev)
      setToast(err instanceof ApiError ? err.message : '변경하지 못했어요')
    } finally {
      setBusyId(null)
    }
  }

  const toggleStar = (p: OrgCatalogItem) => void mutate(p, { starred: !p.starred })
  const setDefault = (p: OrgCatalogItem) => void mutate(p, { starred: true, isDefault: true })

  // 전부 노출로 리셋 — 켠 상품을 일괄 해제(운영자 고정은 유지)해 전체 노출로 되돌린다.
  async function showAll() {
    if (!window.confirm('전부 노출로 되돌릴까요? 전체 상품을 주문할 수 있게 돼요.')) return
    setClearing(true)
    setToast('')
    try {
      await orgCatalogApi.showAll()
      reload()
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : '되돌리지 못했어요')
    } finally {
      setClearing(false)
    }
  }

  return (
    <div className={s.page}>
      <header className={s.head}>
        <h1 className={s.title}>주문서 노출 설정</h1>
        <p className={s.lead}>직원이 주문할 때 보이는 상품을 정해요. 여기서 <b>노출 설정한 상품만</b> 직원이 주문할 수 있어요.</p>
      </header>

      {/* 노출 현황 + 전부 노출 리셋 — 안 쓰는 회사는 전부 노출로 두고 카운트만 보면 된다 */}
      <div className={curated ? `${s.statusBar} ${s.statusOn}` : s.statusBar}>
        <div className={s.statusText}>
          <span className={s.statusCount}>
            노출 중 <b>{shownCount}</b>
            <span className={s.statusTotal}> / 전체 {total}개</span>
          </span>
          <span className={s.statusSub}>
            {curated ? '직원은 노출된 상품만 주문할 수 있어요' : '전부 노출 중 — 자주 쓰는 상품만 켜서 정리할 수 있어요'}
          </span>
        </div>
        {curated ? (
          <button type="button" className={s.showAllBtn} onClick={() => void showAll()} disabled={clearing}>
            {clearing ? '처리 중…' : '전부 노출'}
          </button>
        ) : null}
      </div>

      {/* 큐레이션 후 어떤 유형이 노출 0이면 직원이 그 유형을 주문 못 함 — 명시 경고 */}
      {emptyCats.length > 0 ? (
        <div className={s.warn}>
          <span className={s.warnIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 3.5 21 19H3l9-15.5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="12" cy="16.6" r="0.4" fill="currentColor" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </span>
          <span>
            <b>{emptyCats.map((t) => t.label).join('·')}</b> 유형은 노출한 상품이 없어 직원이 주문할 수 없어요. 상품을 켜거나 <b>전부 노출</b>하세요.
          </span>
        </div>
      ) : null}

      <div className={s.tabs} role="tablist" aria-label="상품 유형">
        {OCC_TABS.map((t) => {
          const st = occStat(t.value)
          const empty = curated && st.total > 0 && st.exposed === 0
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={tab === t.value}
              className={[tab === t.value ? `${s.tab} ${s.on}` : s.tab, empty ? s.tabEmpty : ''].join(' ').trim()}
              onClick={() => setTab(t.value)}
            >
              {t.label}
              <span className={s.tcount}>
                {st.exposed}/{st.total}
              </span>
            </button>
          )
        })}
      </div>

      {toast ? <div className={s.toast}>{toast}</div> : null}

      {error ? (
        <p className={s.muted}>{error}</p>
      ) : loading ? (
        <p className={s.muted}>불러오는 중…</p>
      ) : shown.length === 0 ? (
        <p className={s.muted}>이 유형의 상품이 아직 없어요.</p>
      ) : (
        <div className={s.grid}>
          {shown.map((p) => (
            <div key={p.productId} className={p.starred ? `${s.card} ${s.cardOn}` : s.card}>
              <span
                className={s.pim}
                style={{ backgroundImage: `url(${p.imageUrl ?? defaultProductImage})` }}
                aria-hidden="true"
              />
              {p.starred ? <span className={s.inBadge} aria-hidden="true">노출 중</span> : null}
              {p.locked ? (
                <span className={s.lock} title="운영자가 고정한 상품" aria-label="고정됨">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="5" y="10.5" width="14" height="9.5" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
              ) : null}

              <span className={s.info}>
                <span className={s.name}>{p.name}</span>
                <span className={s.price}>{p.price.toLocaleString('ko-KR')}원</span>
              </span>

              <div className={s.actions}>
                <div className={s.switchRow}>
                  <span className={s.switchLabel}>{p.locked ? '운영자 고정' : '주문서 노출'}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={p.starred}
                    aria-label={p.starred ? '주문서에서 숨기기' : '주문서에 노출하기'}
                    className={p.starred ? `${s.switch} ${s.switchOn}` : s.switch}
                    onClick={() => toggleStar(p)}
                    disabled={busyId === p.productId || (p.locked && p.starred)}
                    title={p.locked && p.starred ? '운영자가 고정한 상품이라 끌 수 없어요' : undefined}
                  >
                    <span className={s.knob} aria-hidden="true" />
                  </button>
                </div>
                {p.starred ? (
                  p.isDefault ? (
                    <span className={s.defaultBadge}>기본상품</span>
                  ) : (
                    <button
                      type="button"
                      className={s.defaultBtn}
                      onClick={() => setDefault(p)}
                      disabled={busyId === p.productId}
                      title="주문 시 이 상품이 먼저 선택돼요"
                    >
                      기본으로
                    </button>
                  )
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
